import { LitElement, html, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { getServices } from "../services/index.ts";

type AuthMode = "signup" | "signin";

@customElement("katanoid-onboarding")
export class KatanoidOnboarding extends LitElement {
  protected override createRenderRoot(): HTMLElement {
    return this;
  }

  @property({ type: String }) step: "signup" = "signup";
  @property({ type: String }) userId = "";
  @property({ type: String }) authMode: AuthMode = "signup";
  @state() private email = "";
  @state() private password = "";
  @state() private authError: string | null = null;
  @state() private loading = false;
  @state() private confirmationSent = false;
  @state() private termsAccepted = false;

  protected override render(): TemplateResult {
    return html`
      <div class="onboarding">
        <div class="onboarding-content">
          ${this.renderSignupStep()}
        </div>
      </div>
    `;
  }

  // ── Sign Up / Sign In ──

  private renderSignupStep(): TemplateResult {
    if (this.confirmationSent) {
      return html`
        <div class="onboarding-step-enter">
          <div class="onboarding-logo">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="width:32px;height:32px;color:var(--text-primary)">
              <rect x="2" y="4" width="20" height="16" rx="2"/>
              <path d="m22 4-10 8L2 4"/>
            </svg>
          </div>
          <h1 class="onboarding-heading">Check your email</h1>
          <p class="onboarding-subheading">
            We sent a confirmation link to <strong>${this.email}</strong>. Click it to activate your account, then come back here.
          </p>
          <button
            class="btn btn--ghost btn--full"
            @click=${() => {
              this.confirmationSent = false;
              this.authError = null;
            }}
          >
            ← Back to sign in
          </button>
        </div>
      `;
    }

    const isSignIn = this.authMode === "signin";

    return html`
      <div class="onboarding-step-enter">
        <button
          class="btn btn--ghost btn--sm onboarding-back"
          @click=${() => this.dispatchEvent(new CustomEvent("back-to-landing", { bubbles: true, composed: true }))}
        >← Back</button>

        <div class="onboarding-logo">
          <svg viewBox="0 0 48 48" fill="none">
            <path d="M24 4L6 14v20l18 10 18-10V14L24 4z" fill="white" opacity="0.9"/>
            <path d="M24 4L6 14l18 10 18-10L24 4z" fill="white"/>
          </svg>
        </div>

        <h1 class="onboarding-heading">
          ${isSignIn ? "Welcome back" : "Create your account"}
        </h1>
        <p class="onboarding-subheading">
          ${isSignIn
            ? "Sign in to continue"
            : "Connect WhatsApp, Telegram, Discord, and more."}
        </p>

        <div class="onboarding-form">
          <div class="social-login-group">
            <button class="social-login-btn" @click=${this.handleGoogleAuth}>
              <svg viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              Continue with Google
            </button>
            ${!isSignIn
              ? html`<p class="social-login-terms-note">
                  By signing up, you agree to our
                  <a href="/terms" class="onboarding-terms__link" @click=${this._navigateTo("/terms")}>Terms</a>
                  and
                  <a href="/privacy" class="onboarding-terms__link" @click=${this._navigateTo("/privacy")}>Privacy Policy</a>.
                </p>`
              : ""}
          </div>

          <div class="social-login-divider">or</div>

          <input
            class="input ${this.authError ? "input--error" : ""}"
            type="email"
            placeholder="Email"
            .value=${this.email}
            @input=${(e: InputEvent) => {
              this.email = (e.target as HTMLInputElement).value;
              this.authError = null;
            }}
            @keydown=${(e: KeyboardEvent) => {
              if (e.key === "Enter") this.handleEmailAuth();
            }}
          />

          <input
            class="input ${this.authError ? "input--error" : ""}"
            type="password"
            placeholder="Password"
            .value=${this.password}
            @input=${(e: InputEvent) => {
              this.password = (e.target as HTMLInputElement).value;
              this.authError = null;
            }}
            @keydown=${(e: KeyboardEvent) => {
              if (e.key === "Enter") this.handleEmailAuth();
            }}
          />

          ${this.authError
            ? html`<p class="input-error-text">${this.authError}</p>`
            : ""}

          ${!isSignIn
            ? html`
              <label class="onboarding-terms">
                <input
                  type="checkbox"
                  class="onboarding-terms__check"
                  .checked=${this.termsAccepted}
                  @change=${(e: Event) => {
                    this.termsAccepted = (e.target as HTMLInputElement).checked;
                    this.authError = null;
                  }}
                />
                <span class="onboarding-terms__text">
                  I agree to the
                  <a href="/terms" class="onboarding-terms__link" @click=${this._navigateTo("/terms")}>Terms of Service</a>
                  and
                  <a href="/privacy" class="onboarding-terms__link" @click=${this._navigateTo("/privacy")}>Privacy Policy</a>
                </span>
              </label>
            `
            : ""}

          <button
            class="btn btn--primary btn--full btn--lg"
            ?disabled=${this.loading || !this.email || !this.password || (!isSignIn && !this.termsAccepted)}
            @click=${this.handleEmailAuth}
          >
            ${this.loading
              ? html`<span class="spinner spinner--sm"></span>`
              : isSignIn
                ? "Sign In"
                : "Create Account"}
          </button>

          <button
            class="btn btn--ghost btn--full"
            @click=${() => {
              const path = isSignIn ? "/signup" : "/login";
              window.history.replaceState(null, "", path);
              window.dispatchEvent(new PopStateEvent("popstate"));
            }}
          >
            ${isSignIn
              ? "Don't have an account? Sign up"
              : "Already have an account? Sign in"}
          </button>
        </div>
      </div>
    `;
  }

  // ── Handlers ──

  private _navigateTo = (path: string) => (e: Event): void => {
    e.preventDefault();
    window.history.pushState(null, "", path);
    window.dispatchEvent(new PopStateEvent("popstate"));
  };

  private async handleEmailAuth(): Promise<void> {
    if (!this.email || !this.password) return;
    if (this.authMode === "signup" && !this.termsAccepted) {
      this.authError = "Please accept the Terms of Service and Privacy Policy to continue.";
      return;
    }
    this.loading = true;
    this.authError = null;

    try {
      const { auth } = getServices();
      const result =
        this.authMode === "signup"
          ? await auth.signUp(this.email, this.password)
          : await auth.signIn(this.email, this.password);

      if (result.error) {
        if (result.error === "confirmation_required") {
          this.confirmationSent = true;
          this.loading = false;
          return;
        }
        this.authError = result.error;
        this.loading = false;
        return;
      }

      this.loading = false;
      this.dispatchEvent(new CustomEvent("auth-complete", { bubbles: true, composed: true }));
    } catch (err) {
      this.authError = String(err);
      this.loading = false;
    }
  }

  private async handleGoogleAuth(): Promise<void> {
    try {
      const { auth } = getServices();
      const { error } = await auth.signInWithOAuth("google");
      if (error) this.authError = error;
      else this.dispatchEvent(new CustomEvent("auth-complete", { bubbles: true, composed: true }));
    } catch (err) {
      this.authError = String(err);
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "katanoid-onboarding": KatanoidOnboarding;
  }
}
